<template>
  <Transition name="modal">
    <div v-if="show" class="modal-overlay" @click.self="$emit('cancel')" @keydown.esc="$emit('cancel')">
      <div class="modal-card" role="dialog" aria-modal="true">
        <h2 class="modal-title">{{ title }}</h2>
        <div class="modal-body">
          <slot />
        </div>
        <div class="modal-actions">
          <button class="modal-btn modal-btn-cancel" @click="$emit('cancel')">
            {{ cancelText }}
          </button>
          <button
            class="modal-btn modal-btn-confirm"
            :class="{ 'modal-btn-danger': danger }"
            @click="$emit('confirm')"
          >
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { watch, onUnmounted } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  confirmText: {
    type: String,
    default: 'Confirm',
  },
  cancelText: {
    type: String,
    default: 'Cancel',
  },
  danger: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['confirm', 'cancel']);

function handleEsc(e) {
  if (e.key === 'Escape') {
    emit('cancel');
  }
}

watch(() => props.show, (val) => {
  if (val) {
    document.addEventListener('keydown', handleEsc);
  } else {
    document.removeEventListener('keydown', handleEsc);
  }
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleEsc);
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--bg-overlay);
  z-index: var(--z-index-modal);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-card {
  background: var(--bg-dropdown);
  border-radius: var(--border-radius-xl);
  padding: var(--padding-modal);
  min-width: 320px;
  max-width: 440px;
  width: 90%;
}

.modal-title {
  font-size: var(--font-size-h2);
  font-weight: var(--font-weight-semibold);
  margin: 0 0 var(--spacing-lg);
}

.modal-body {
  color: var(--color-white-70);
  font-size: var(--font-size-body);
  margin-bottom: var(--spacing-2xl);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-lg);
}

.modal-btn {
  font-family: var(--font-family-primary);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  padding: var(--padding-btn-md);
  border: none;
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: opacity var(--transition-fast);
}

.modal-btn:hover {
  opacity: 0.85;
}

.modal-btn-cancel {
  background: var(--color-white-10);
  color: var(--color-white);
}

.modal-btn-confirm {
  background: var(--gradient-primary);
  color: var(--color-white);
}

.modal-btn-danger {
  background: var(--color-cancel);
}

/* Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity var(--transition-fast);
}

.modal-enter-active .modal-card,
.modal-leave-active .modal-card {
  transition: transform var(--transition-fast);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-card {
  transform: scale(0.95);
}

.modal-leave-to .modal-card {
  transform: scale(0.95);
}
</style>
